import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  NumberIncrementStepper,
  NumberInputStepper,
  NumberInputField,
  NumberInput,
  Box,
  FormLabel,
  NumberDecrementStepper,
  useDisclosure,
  Text,
  HStack,
  VStack,
  useToast
} from '@chakra-ui/react'
import { FC, useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { FaShoppingCart, FaArrowLeft } from 'react-icons/fa'

import useMrpPurchase from '@hook/mrp/use-purchase'

import ForaCronogramaReview from '@component/Modules/Mrp/ForaCronogramaReview'
import JustificationTable, {
  ILotesSemRecomendacao
} from '@component/Modules/Mrp/JustificationTable'
import Filter from '@component/Shared/Filter'

import { getIsUsuarioCompras } from '@helper/auth/getUserTokenClaims'
import { AlteredSku } from '@helper/mrp/justifications'
import mrp from '@service/mrp'

import { ILayoutActionSuppliers } from '@base/@types/components/Layout/Tools'

import LayoutToolsButton from '../Components/Button'

const ActionPurchaseCreateOrderSap: FC<ILayoutActionSuppliers> = () => {
  const {
    handleUpdateSapParams,
    paramsSap,
    exportDataSap,
    supplyMethod,
    discountSupply,
    alteredSkus,
    handleUpdateJustification,
    handleBulkUpdateApprovers,
    params,
    appliedParams,
    justificativaForaCronograma,
    setJustificativaForaCronograma,
    sendingOrder
  } = useMrpPurchase()
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [step, setStep] = useState(1)

  const [discountValue, setDiscountValue] = useState(
    paramsSap.desconto.toString()
  )
  const [quantidadePedidos, setQuantidadePedidos] = useState('')

  const [individualApprovers, setIndividualApprovers] = useState<
    Map<number, string>
  >(new Map())
  // Itens sem recomendação de compra: lotes calculados pelo backend na prévia do aprovador
  const [lotesSemRecomendacao, setLotesSemRecomendacao] = useState<
    Map<number, ILotesSemRecomendacao>
  >(new Map())
  const [loadingApprovers, setLoadingApprovers] = useState(false)
  const [approversError, setApproversError] = useState<string | null>(null)

  // Trava contra duplo clique em "Prosseguir"/"Enviar Pedido"
  const proceedingRef = useRef(false)
  const [proceeding, setProceeding] = useState(false)

  // Forma de pagamento anterior: o desconto padrão só é aplicado quando ela muda
  const previousMetodoRef = useRef(paramsSap.metodoPagamento[0]?.value)

  // Verifica se está utilizando cronograma usando os params aplicados no último filtro.
  // Somente o usuário compras trabalha com cronograma (demais seguem o fluxo direto).
  const isUtilizarCronograma = useMemo(() => {
    if (!getIsUsuarioCompras()) return false
    const activeParams = appliedParams || params
    const cron = activeParams.utilizarCronograma
    const val = Array.isArray(cron) ? cron[0]?.value : (cron as any)?.value
    return val === '1'
  }, [appliedParams, params])

  // Mesma regra que exibe o filtro "Utilizar Cronograma" no MRP Compras
  const isUsuarioCompras = useMemo(() => getIsUsuarioCompras(), [])

  // Comprador sem cronograma: justificativa única obrigatória; as alterações de
  // quantidade seguem as mesmas regras de tolerância e alçada do cronograma
  const isForaCronograma = useMemo(
    () => !isUtilizarCronograma && getIsUsuarioCompras(),
    [isUtilizarCronograma]
  )

  // Justificativas por SKU (e prévia do aprovador) quando o Comprador tem alterações,
  // com ou sem cronograma
  const needsSkuJustification =
    (isUtilizarCronograma || isForaCronograma) && alteredSkus.length > 0

  const needsJustification = isForaCronograma || needsSkuJustification

  const isStep2ConfirmDisabled = useMemo(() => {
    if (sendingOrder) return true
    if (isForaCronograma && !justificativaForaCronograma.trim()) return true
    if (!needsSkuJustification) return false
    return (
      loadingApprovers ||
      !!approversError ||
      alteredSkus.some((sku: AlteredSku) => !sku.justificativa)
    )
  }, [
    sendingOrder,
    alteredSkus,
    needsSkuJustification,
    isForaCronograma,
    justificativaForaCronograma,
    loadingApprovers,
    approversError
  ])

  const fetchApproversBeforeStep2 = async () => {
    if (alteredSkus.length === 0) return

    setLoadingApprovers(true)
    setApproversError(null)
    try {
      const listaPedido = alteredSkus.map((sku: AlteredSku) => ({
        empresaId: sku.idEmpresa,
        lojaId: sku.loja.id,
        produtoId: sku.produto.id,
        quantidadeRecomendada: sku.quantidadeOriginal,
        quantidadeDesejada: sku.novaQuantidade
      }))

      const response = await mrp.postApproverOrder({
        utilizarCronogramaCompras: isUtilizarCronograma,
        listaPedido
      })
      const responseData = response?.data?.data

      const approversMap = new Map<number, string>()
      const lotesMap = new Map<number, ILotesSemRecomendacao>()
      if (Array.isArray(responseData)) {
        responseData.forEach((item: any) => {
          // Só associa pelo SKU exato; sem correspondência o item fica pendente de aprovador
          const correspondingSku = alteredSkus.find(
            (sku: AlteredSku) =>
              sku.idEmpresa === item.empresaId &&
              sku.loja.id === item.lojaId &&
              sku.produto.id === item.produtoId
          )
          if (!correspondingSku) return

          if (item.usuarioAprovadorEmail) {
            approversMap.set(correspondingSku.id, item.usuarioAprovadorEmail)
          }
          if (item.itemSemRecomendacao) {
            lotesMap.set(correspondingSku.id, {
              loteMultiplo: item.loteMultiplo ?? null,
              quantidadeLotes: item.quantidadeLotes ?? null
            })
          }
        })
      }

      setIndividualApprovers(approversMap)
      setLotesSemRecomendacao(lotesMap)
      handleBulkUpdateApprovers(approversMap)
    } catch (error: any) {
      setIndividualApprovers(new Map())
      setLotesSemRecomendacao(new Map())
      // Ex.: Lote Múltiplo não parametrizado ou nenhuma alçada cobre os lotes/percentual
      setApproversError(
        error?.response?.data?.message ||
          error?.message ||
          'Não foi possível consultar os aprovadores do pedido.'
      )
    } finally {
      setLoadingApprovers(false)
    }
  }

  const getApproverName = useCallback(
    (sku: AlteredSku) =>
      individualApprovers.get(sku.id) ||
      sku.usuarioAprovadorEmail ||
      'Pendente de aprovador',
    [individualApprovers]
  )

  useEffect(() => {
    const selectedMethodId = paramsSap.metodoPagamento[0]?.value
    if (previousMetodoRef.current === selectedMethodId) return
    previousMetodoRef.current = selectedMethodId

    // O provider já aplica o desconto padrão ao trocar a forma de pagamento;
    // aqui apenas sincroniza o campo local.
    if (
      discountSupply &&
      selectedMethodId !== undefined &&
      selectedMethodId === discountSupply.metodoPagamentoId
    ) {
      setDiscountValue(discountSupply.desconto.toString())
    }
  }, [paramsSap.metodoPagamento, discountSupply])

  const handleDiscountBlur = () => {
    // Regra do mínimo: com a forma de pagamento padrão do fornecedor o desconto
    // não pode ser menor que o cadastrado
    const selectedMethodId = paramsSap.metodoPagamento[0]?.value
    if (
      discountSupply &&
      selectedMethodId !== undefined &&
      selectedMethodId === discountSupply.metodoPagamentoId &&
      Number(discountValue) < Number(discountSupply.desconto)
    ) {
      setDiscountValue(discountSupply.desconto.toString())
    }
    handleUpdateSapParams({ name: 'desconto', value: discountValue })
  }

  const clearJustifications = () => {
    alteredSkus.forEach(sku => {
      if (sku.justificativa || sku.observacao) {
        handleUpdateJustification(sku.id, '', '')
      }
    })
  }

  const resetModalState = () => {
    setStep(1)
    setIndividualApprovers(new Map())
    setLotesSemRecomendacao(new Map())
    setApproversError(null)
    setDiscountValue(paramsSap.desconto.toString())
    setQuantidadePedidos(
      paramsSap.QuantidadeMaximaItensPedido
        ? paramsSap.QuantidadeMaximaItensPedido.toString()
        : ''
    )
    previousMetodoRef.current = paramsSap.metodoPagamento[0]?.value
  }

  const handleModalClose = () => {
    // Não fecha no meio do envio
    if (sendingOrder) return

    clearJustifications()
    onClose()
  }

  const handleConfirm = async () => {
    if (isForaCronograma && !justificativaForaCronograma.trim()) {
      toast({
        title: 'Justificativa obrigatória',
        description:
          'Informe a justificativa para não utilizar o Cronograma de Compras.',
        status: 'warning',
        duration: 4000,
        isClosable: true
      })
      return
    }

    // Validação: Comprador com alterações, verificar se todas têm justificativa
    if (needsSkuJustification) {
      const skusWithoutJustification = alteredSkus.filter(
        sku => !sku.justificativa
      )
      if (skusWithoutJustification.length > 0) {
        toast({
          title: 'Justificativas obrigatórias',
          description: `${skusWithoutJustification.length} item(s) com quantidade alterada precisam de justificativa.`,
          status: 'warning',
          duration: 4000,
          isClosable: true
        })
        return
      }
    }

    // Quantidade máxima de itens por pedido: vazio = sem limite; se informada precisa ser inteira e positiva
    if (quantidadePedidos.trim() !== '') {
      const quantidadeMaximaItens = Number(quantidadePedidos)
      if (
        !Number.isInteger(quantidadeMaximaItens) ||
        quantidadeMaximaItens <= 0
      ) {
        toast({
          title: 'Atenção',
          description:
            'Quantidade máxima de itens por pedido deve ser um número inteiro maior que zero.',
          status: 'warning',
          duration: 4000,
          isClosable: true
        })
        return
      }
    }

    const success = await exportDataSap()
    if (success) {
      onClose()
    }
  }

  const handleProceed = async () => {
    if (proceedingRef.current || sendingOrder) return
    proceedingRef.current = true
    setProceeding(true)

    try {
      // Step 2 (justificativas): Comprador fora do cronograma (justificativa
      // única) e/ou com alterações de quantidade (justificativa por item)
      if (needsJustification) {
        // Busca aprovadores ANTES de ir para step 2 (só há prévia com alterações)
        if (needsSkuJustification) await fetchApproversBeforeStep2()
        setStep(2)
        return
      }

      await handleConfirm()
    } finally {
      proceedingRef.current = false
      setProceeding(false)
    }
  }

  const handleOpenModal = () => {
    clearJustifications()
    resetModalState()
    onOpen()
  }

  return (
    <>
      <LayoutToolsButton label="Criar Pedido" onClick={handleOpenModal} />
      <Modal
        isOpen={isOpen}
        onClose={handleModalClose}
        size={step === 1 ? '4xl' : 'full'}
        scrollBehavior="inside"
        closeOnOverlayClick={!sendingOrder}
      >
        <ModalOverlay />
        <ModalContent
          bgColor="white"
          borderRadius="lg"
          maxH={step === 1 ? '80vh' : '95vh'}
          maxW={step === 1 ? '60vw' : '95vw'}
        >
          <ModalHeader>
            <HStack spacing={3} justify="space-between" align="center">
              <HStack spacing={3}>
                <FaShoppingCart color="#38a169" />
                <Text>
                  {step === 1
                    ? 'Criar Pedido de Compra'
                    : isForaCronograma
                    ? 'Justificativa fora do Cronograma de Compras'
                    : 'Justificativa de Alteração'}
                </Text>
              </HStack>

              {/* Indicador de passos: só quando existe o passo de justificativas
                  (cronograma com alterações ou pedido fora do cronograma) */}
              {needsJustification && (
                <HStack spacing={2} fontSize="sm" color="gray.500" pr={8}>
                  <Box
                    px={2}
                    py={1}
                    borderRadius="md"
                    bg={step === 1 ? 'blue.100' : 'gray.100'}
                    color={step === 1 ? 'blue.700' : 'gray.500'}
                    fontWeight={step === 1 ? 'bold' : 'normal'}
                  >
                    1. Configurações
                  </Box>
                  <Text color="gray.300">→</Text>
                  <Box
                    px={2}
                    py={1}
                    borderRadius="md"
                    bg={step === 2 ? 'green.100' : 'gray.100'}
                    color={step === 2 ? 'green.700' : 'gray.500'}
                    fontWeight={step === 2 ? 'bold' : 'normal'}
                  >
                    2. Justificativas
                  </Box>
                </HStack>
              )}
            </HStack>
          </ModalHeader>
          <ModalCloseButton isDisabled={sendingOrder} />

          <ModalBody py={6}>
            {step === 1 ? (
              <VStack spacing={6} align="stretch">
                {/* Informação sobre o modo atual: mesma regra do filtro "Utilizar Cronograma"
                    (só o usuário compras trabalha com cronograma) */}
                {isUsuarioCompras && (
                  <Box
                    p={4}
                    bg={isUtilizarCronograma ? 'blue.50' : 'yellow.50'}
                    borderRadius="md"
                    border="2px solid"
                    borderColor={
                      isUtilizarCronograma ? 'blue.200' : 'yellow.200'
                    }
                  >
                    <Text
                      fontSize="sm"
                      fontWeight="bold"
                      color={isUtilizarCronograma ? 'blue.700' : 'yellow.700'}
                    >
                      {isUtilizarCronograma
                        ? 'Utilizando Cronograma'
                        : 'Não utilizando Cronograma'}
                    </Text>
                    <Text
                      fontSize="xs"
                      color={isUtilizarCronograma ? 'blue.600' : 'yellow.600'}
                    >
                      {isUtilizarCronograma
                        ? 'Alterações de quantidade irão para aprovação conforme cronograma configurado'
                        : isForaCronograma
                        ? 'Justificativa obrigatória. Alterações de quantidade seguem as regras de tolerância e alçada'
                        : 'Pedidos serão enviados diretamente sem necessidade de justificativa'}
                    </Text>
                  </Box>
                )}

                <Filter
                  label="Forma de Pagamento"
                  options={supplyMethod.map(paymentMethod => ({
                    label: `${paymentMethod.codigo} - ${paymentMethod.descricao}`,
                    value: paymentMethod.id
                  }))}
                  value={paramsSap.metodoPagamento}
                  isMulti
                  onChange={value =>
                    handleUpdateSapParams({ name: 'metodoPagamento', value })
                  }
                />

                <FormControl id="discount">
                  <FormLabel fontWeight="medium">Desconto (%)</FormLabel>
                  <NumberInput
                    value={discountValue}
                    onChange={value => setDiscountValue(value)}
                    onBlur={handleDiscountBlur}
                    min={0}
                    max={100}
                  >
                    <NumberInputField borderColor="gray.300" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
                <Box w="100%">
                  <FormControl id="quantidadePedidos">
                    <FormLabel>Qtd máx. de itens por pedido</FormLabel>
                    <NumberInput
                      borderColor="blackAlpha.400"
                      value={quantidadePedidos}
                      onChange={value => setQuantidadePedidos(value)}
                      onBlur={(event: any) => {
                        handleUpdateSapParams({
                          name: 'QuantidadeMaximaItensPedido',
                          value: event.target.value
                        })
                      }}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </Box>
              </VStack>
            ) : (
              <VStack spacing={4} align="stretch">
                {approversError && !loadingApprovers && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription flex={1} fontSize="sm">
                      {approversError} O envio fica bloqueado até a consulta do
                      aprovador ser concluída.
                    </AlertDescription>
                    <Button
                      size="sm"
                      colorScheme="red"
                      variant="outline"
                      onClick={fetchApproversBeforeStep2}
                    >
                      Tentar novamente
                    </Button>
                  </Alert>
                )}
                {isForaCronograma && (
                  <ForaCronogramaReview
                    justificativa={justificativaForaCronograma}
                    onJustificativaChange={setJustificativaForaCronograma}
                  />
                )}
                {needsSkuJustification && (
                  <JustificationTable
                    alteredSkus={alteredSkus}
                    onUpdateJustification={handleUpdateJustification}
                    showPercent
                    lotesSemRecomendacao={lotesSemRecomendacao}
                    getApproverName={
                      loadingApprovers
                        ? () => 'Consultando...'
                        : getApproverName
                    }
                  />
                )}
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            {step === 1 ? (
              <Button
                variant="solid"
                colorScheme={needsJustification ? 'blue' : 'green'}
                onClick={handleProceed}
                isLoading={proceeding || sendingOrder}
                isDisabled={proceeding || sendingOrder}
                size="md"
                px={6}
                ml="auto"
              >
                {needsJustification ? 'Prosseguir' : 'Enviar Pedido'}
              </Button>
            ) : (
              <>
                <Button
                  variant="solid"
                  colorScheme="gray"
                  onClick={() => setStep(1)}
                  isDisabled={sendingOrder}
                  leftIcon={<FaArrowLeft />}
                  size="md"
                  mr={3}
                >
                  Voltar
                </Button>

                <Button
                  colorScheme="green"
                  onClick={handleConfirm}
                  isDisabled={isStep2ConfirmDisabled}
                  isLoading={sendingOrder}
                  size="md"
                  px={6}
                  title={
                    approversError
                      ? 'Não foi possível consultar o aprovador. Tente novamente.'
                      : isStep2ConfirmDisabled && needsJustification
                      ? 'Preencha todas as justificativas obrigatórias'
                      : 'Confirmar e gerar pedido no SAP'
                  }
                >
                  {isUtilizarCronograma
                    ? 'Confirmar com Cronograma'
                    : isForaCronograma
                    ? 'Confirmar sem Cronograma'
                    : 'Confirmar e Gerar Pedido'}
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default ActionPurchaseCreateOrderSap
